import {
  type GloveCommandBody,
  type MaiButtons,
  type MaiField,
  type MaiListScreen,
  type MaiScreen,
  type MaiWorkflowScreen,
} from './screens';

/** Streams API 3.8.10 field limits for MAI templates. */
export const MAI_LIMITS = {
  header: 100,
  content: 200,
  tagline: 30,
  message: 100,
  buttonText: 30,
  title: 100,
  listText: 100,
  listItems: 10,
} as const;

const truncate = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

// MAI only knows four preset field states (FOCUSED, SUCCESS, WARNING, ERROR),
// each with a fixed icon and color; custom colors are not rendered (device-tested).
const fieldBody = ({ header, text, state, highlighted }: MaiField): Record<string, unknown> => ({
  ...(header !== undefined && header !== '' && { text_header: truncate(header, MAI_LIMITS.header) }),
  text_content: truncate(text, MAI_LIMITS.content),
  ...(state !== undefined && {
    state: { type: state.toUpperCase(), ...(highlighted === true && { highlighted: true }) },
  }),
});

interface WorkflowLayout {
  template: string;
  slots: string[][];
}

// Workflow templates by line shape (fields per line, top to bottom): the field
// slots each template offers, line by line. The one place to change a template
// variant after reviewing other layouts on a device.
const workflowLayouts: Record<string, WorkflowLayout> = {
  '1': { template: 'pg_work1_t1', slots: [['field_main']] },
  '2': { template: 'pg_work2_t4', slots: [['field_left', 'field_right']] },
  '1,1': { template: 'pg_work2_t1', slots: [['field_top'], ['field_bottom']] },
  '2,1': { template: 'pg_work3_t2', slots: [['field_top_left', 'field_top_right'], ['field_bottom']] },
  '1,2': { template: 'pg_work3_t3', slots: [['field_top'], ['field_bottom_left', 'field_bottom_right']] },
  '2,2': {
    template: 'pg_work4_t5',
    slots: [
      ['field_top_left', 'field_top_right'],
      ['field_bottom_left', 'field_bottom_right'],
    ],
  },
  '1,1,1': { template: 'pg_work3_t1', slots: [['field_top'], ['field_middle'], ['field_bottom']] },
  '1,1,2': {
    template: 'pg_work4_t1',
    slots: [['field_top'], ['field_middle'], ['field_bottom_left', 'field_bottom_right']],
  },
  '1,2,1': {
    template: 'pg_work4_t2',
    slots: [['field_top'], ['field_middle_left', 'field_middle_right'], ['field_bottom']],
  },
  '1,2,2': {
    template: 'pg_work5_t1',
    slots: [['field_top'], ['field_middle_left', 'field_middle_right'], ['field_bottom_left', 'field_bottom_right']],
  },
};

// the first line on 70% of the screen, the rest sharing the 30% below
const largeTopRowLayouts: Record<string, WorkflowLayout> = {
  '1': workflowLayouts['1'],
  '1,1': { template: 'pg_work2_t3', slots: [['field_top'], ['field_bottom']] },
  '1,2': { template: 'pg_work3_t4', slots: [['field_top'], ['field_bottom_left', 'field_bottom_right']] },
};

/** Fields line by line; empty lines are dropped, without any field one empty field is shown. */
const workflowView = ({ title, rows, largeTopRow }: MaiWorkflowScreen): Record<string, unknown> => {
  const filled = rows.filter((row) => row.length > 0);
  const shownRows = filled.length > 0 ? filled : [[{ text: '' }]];

  const shape = shownRows.map((row) => row.length).join(',');
  const layout = (largeTopRow === true ? largeTopRowLayouts : workflowLayouts)[shape];
  if (layout === undefined) {
    throw new Error(
      `No MAI workflow template for the line shape ${shape}${largeTopRow === true ? ' (large top)' : ''}`,
    );
  }

  return {
    [layout.template]: {
      ...(title !== undefined && title !== '' && { title: truncate(title, MAI_LIMITS.title) }),
      ...Object.fromEntries(
        shownRows.flatMap((row, rowIndex) => row.map((field, i) => [layout.slots[rowIndex][i], fieldBody(field)])),
      ),
    },
  };
};

const listItemBody = (text: string, trailing?: string, underline?: string): Record<string, unknown> => ({
  text_main: truncate(text, MAI_LIMITS.listText),
  ...(trailing !== undefined && trailing !== '' && { text_trailing: truncate(trailing, MAI_LIMITS.listText) }),
  ...(underline !== undefined && underline !== '' && { text_underline: truncate(underline, MAI_LIMITS.listText) }),
});

/**
 * Items with an id notify on a tap. MAI refuses lists with more than ten items
 * (device-verified), so beyond that nine items and a plain hint are shown.
 */
const listView = ({ header, items }: MaiListScreen): Record<string, unknown> => {
  const fits = items.length <= MAI_LIMITS.listItems;
  const shown = fits ? items : items.slice(0, MAI_LIMITS.listItems - 1);
  const tappable = items.some((item) => item.id !== undefined);

  return {
    pg_list_t1: {
      header: truncate(header, MAI_LIMITS.title),
      items: [
        ...shown.map((item) => ({
          ...(item.id !== undefined && { ref_id: item.id }),
          ...listItemBody(item.text, item.trailing, item.underline),
          ...(item.id !== undefined && { action_on_click: { basic: 'NOTIFY' } }),
        })),
        ...(fits
          ? []
          : [{ text_main: `+${items.length - shown.length} more${tappable ? ' - select in the app' : ''}` }]),
      ],
    },
  };
};

// A press (NOTIFY) arrives as a button_pressed event carrying the button's id as
// ref_id (device-verified), which the provider reports to its action listeners.
const buttonsBody = (buttons: MaiButtons): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(buttons).map(([button, { id, label, color }]) => [
      button,
      {
        ref_id: id,
        text: truncate(label, MAI_LIMITS.buttonText),
        color,
        action_on_single_click: { basic: 'NOTIFY' },
      },
    ]),
  );

// Every screen and view gets a stable reference id (EKKO_<KIND>). Physical
// button presses and list taps report their own id instead.
const displayV2Body = (
  kind: MaiScreen['kind'],
  view: Record<string, unknown>,
  buttons?: MaiButtons,
): GloveCommandBody => {
  const screenRef = `EKKO_${kind.toUpperCase()}`;
  const viewRef = `${screenRef}_VIEW`;

  return {
    api_version: '3.0',
    event_type: 'display_v2!',
    ref_id: screenRef,
    active_screen_view: viewRef,
    screen_views: [{ ref_id: viewRef, ...view }],
    ...(buttons !== undefined && { action_buttons_mode: 'SHARED', action_buttons: buttonsBody(buttons) }),
  };
};

/** MAI `display_v2!` body for a screen (see README.md, "Displays"). */
export const renderMai = (screen: MaiScreen): GloveCommandBody => {
  switch (screen.kind) {
    case 'workflow':
      return displayV2Body('workflow', workflowView(screen), screen.buttons);
    case 'list':
      return displayV2Body('list', listView(screen), screen.buttons);
    case 'notification':
      // a notification with the SCAN icon and a message never renders on MAI
      // (device-verified), so scan prompts are workflows
      return displayV2Body('notification', {
        pg_ntf_t1: {
          icon: screen.type.toUpperCase(),
          tagline: truncate(screen.tagline, MAI_LIMITS.tagline),
          ...(screen.message !== undefined &&
            screen.message !== '' && { message: truncate(screen.message, MAI_LIMITS.message) }),
        },
      });
  }
};
